/**
 * What is left of the mission targets. Each wreck keeps the one shape that made the thing
 * recognisable - the swept wing, the dish, the missile, the arch - broken and burnt, so the
 * player can see at a glance which target he has dealt with. Everything stays inside the old
 * footprint. Scorched surfaces use the ink ramp; a little of the original metal shows through.
 */

import type { Pcg32 } from '../../sim/rng';
import type { PropKind } from '../../sim/types';
import type { Palette } from '../palette';
import { HANGAR_HALF_D } from './airfield';
import { wreck } from './depot';
import type { KitBuilder } from './flora';
import { JET_FIN, JET_FIN_FLASH, JET_WING } from './jet';
import { MeshBuilder, place } from './MeshBuilder';
import { mirrorX, prism } from './shapes';
import { bunkerWreck, mgNestWreck, turretBunkerWreck } from './wrecksField';
import { wallBreach } from './walls';
import { debris } from './wreckParts';
import {
  bargeWreck, fuelWagonWreck, locomotiveWreck, mobileLauncherWreck, staffCarWreck,
} from './wrecksVehicles';
import {
  bridgePierWreck, casemateWreck, fieldGunWreck, generatorHutWreck, gunPitWreck, laboratoryWreck, relayMastWreck,
  safeWreck,
} from './wrecksWorks';


/** Burnt airframe: back broken, one wing down, the other torn off, fin fallen with its flash still showing. */
const aircraftWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const burnt = { all: p.color('ink.1'), top: p.color('metal.lo') };
  const char = { top: p.color('ink.1'), side: p.color('ink.0') };
  mb.box(1.3, 1.1, 4.2, burnt, place(0.15, 0.1, -2.0, 0.1, 1, 0, 0.2));
  mb.box(1.0, 0.95, 2.6, burnt, place(-0.45, 0.05, 2.5, -0.4, 1, 0.12, -0.1));
  mb.cylinder(0.5, 0, 1.3, 6, p.color('ink.1'), place(-1.3, 0.45, 4.0, -0.9, 1, Math.PI / 2 + 0.2));
  // Ribs of the burnt-out centre section, standing between the two halves.
  for (const z of [0.3, 0.9]) mb.box(1.1, 0.9, 0.12, p.color('ink.0'), place(0, 0.1, z, 0.2));
  // Starboard wing still on, its tip on the ground; port wing torn off and thrown forward.
  const scorched = { top: p.color('metal.lo'), side: p.color('ink.0') };
  mb.with(place(0, 0.75, 0, 0, 1, 0, -0.17), () => prism(mb, JET_WING, 0, 0.16, scorched));
  mb.box(0.8, 0.05, 1.2, p.color('enemyAccent.0'), place(2.85, 0.42, -1.75, 0, 1, 0, -0.17));
  mb.with(place(0.2, 0.06, 1.0, 0.3), () => prism(mb, mirrorX(JET_WING), 0, 0.16, char));
  // The fin lies flat on the port side of the tail, the enemy's flash still showing at its tip.
  type Outline = ReadonlyArray<readonly [number, number]>;
  const flat = (o: Outline): Array<[number, number]> => o.map(([h, z]) => [-(h - 1.0), z]);
  prism(mb, flat(JET_FIN), 0.06, 0.24, scorched);
  prism(mb, flat(JET_FIN_FLASH), 0.06, 0.28, { top: p.color('enemyAccent.0'), side: p.color('ink.0') });
  debris(mb, p, rng, 6, 3.6, 4.6);
  return mb;
};

/** Toppled launcher: pad scorched, turntable off its seat, the rail on its side, one missile broken on the ground. */
const samWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  mb.cylinder(1.6, 1.6, 0.35, 10, p.color('concrete.lo'), undefined, p.color('ink.1'));
  mb.cylinder(1.15, 1.05, 0.5, 8, p.color('ink.1'), place(0.2, 0.3, -0.1, 0, 1, 0.1, 0.32), p.color('metal.lo'));
  const yaw = rng.range(-0.6, 0.6);
  mb.with(place(-0.2, 0.5, 0.1, yaw, 1, Math.PI / 2 - 0.35, 0.5), () => {
    mb.box(1.5, 2.3, 0.16, p.color('metal.lo'), place(0, -1.1, 0.2));
    mb.cylinder(0.17, 0.17, 2.0, 6, p.color('ink.1'), place(0.3, -1.0, 0), p.color('ink.0'));
  });
  // The missile that fell clear: body and nose apart, the warm tip still telling what it was.
  mb.cylinder(0.17, 0.17, 1.3, 6, p.color('metal.mid'), place(-1.0, 0.52, 0.3, 0.9, 1, Math.PI / 2), p.color('ink.1'));
  mb.cylinder(0.17, 0, 0.55, 6, p.color('fire.0'), place(0.5, 0.52, 1.0, 1.7, 1, Math.PI / 2));
  debris(mb, p, rng, 4, 1.2, 1.2);
  return mb;
};

/** Radar: the mast snapped above the plinth, the dish face-down beside it. */
const radarWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  mb.box(1.6, 0.5, 1.6, { all: p.color('concrete.lo'), top: p.color('ink.1') });
  mb.cylinder(0.22, 0.2, 0.9, 6, p.color('ink.1'), place(0, 0.5, 0, 0, 1, 0.12), p.color('ink.0'));
  const a = rng.range(-0.5, 0.5);
  mb.cylinder(0.2, 0.16, 1.2, 6, p.color('metal.lo'), place(0.1, 0.62, 0, a, 1, Math.PI / 2 - 0.1));
  // Dish on its rim: a shallow cone tipped hard over, pale inside still showing.
  const tipped = place(-0.15, 0.55, 0.45, a + 0.4, 1, 0.5, 0.2);
  mb.cylinder(1.3, 0.2, 0.5, 10, p.color('metal.mid'), tipped, p.color('ink.1'));
  debris(mb, p, rng, 3, 0.8, 0.8);
  return mb;
};

/** Pumping station: the block split in two and sagging apart, the slab fallen in, pipes burst upward. */
const pumpWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const shell = { all: p.color('concrete.lo'), top: p.color('ink.1'), front: p.color('concrete.mid') };
  mb.box(2.2, 1.7, 4.2, shell, place(-1.45, -0.1, 0, 0, 1, 0, 0.14));
  mb.box(2.0, 1.3, 4.0, shell, place(1.5, -0.1, 0.1, 0, 1, 0, -0.2));
  mb.box(0.9, 0.5, 3.6, p.color('ink.0'), place(0, 0, 0)); // the burnt-out gap between the halves
  const slab = { all: p.color('concrete.mid'), top: p.color('concrete.hi') };
  mb.box(2.6, 0.3, 2.4, slab, place(-0.5, 0.9, -0.6, 0.2, 1, 0.3, 0.35));
  mb.box(2.2, 0.3, 2.0, { ...slab, top: p.color('ink.1') }, place(1.0, 0.7, 1.0, -0.3, 1, -0.2, -0.3));
  for (const sx of [-1, 1]) {
    mb.cylinder(0.4, 0.4, 1.1, 8, p.color('metal.lo'), place(sx * 2.6, 0.5, 0, 0, 1, 0, sx * -0.9), p.color('ink.0'));
  }
  mb.cylinder(0.65, 0.65, 0.1, 8, p.color('enemyAccent.0'), place(1.6, 0.05, 2.0, 0, 1, 0.1), p.color('enemyAccent.0'));
  debris(mb, p, rng, 5, 2.6, 2.1);
  return mb;
};

/** Hangar: walls broken down to stubs, the arch collapsed onto the floor in sheets, one rib still leaning. */
const hangarWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const wall = { all: p.color('metal.lo'), top: p.color('ink.1') };
  const halfD = HANGAR_HALF_D;
  for (const sx of [-1, 1]) {
    for (let z = -halfD + 1; z < halfD; z += 2.2) mb.box(0.3, rng.range(0.5, 2.1), 2.0, wall, place(sx * 6.85, 0, z));
  }
  for (let i = 0; i < 7; i++) mb.box(2.0, rng.range(0.4, 2.4), 0.3, wall, place(-6 + i * 2, 0, -halfD + 0.15));
  mb.box(13.4, 0.06, halfD * 2, p.color('ink.1'));
  // Roof sheets where they fell: big, tilted, overlapping - still visibly curved metal.
  for (let i = 0; i < 6; i++) {
    const sheet = { all: p.color('ink.1'), top: p.color(i % 2 ? 'metal.lo' : 'metal.mid') };
    const x = rng.range(-4.2, 4.2);
    const y = rng.range(0.1, 0.5);
    const z = rng.range(-halfD + 1.8, halfD - 2.6);
    const at = place(x, y, z, rng.range(-0.5, 0.5), 1, rng.range(-0.22, 0.22), rng.range(-0.25, 0.25));
    mb.box(rng.range(3.0, 4.6), 0.12, rng.range(2.4, 3.6), sheet, at);
  }
  // One rib, fallen back into the shed but still an arch: the shape that says "hangar".
  mb.with(place(0, 0, halfD - 1.4, 0, 1, -0.95), () => {
    mb.box(0.3, 2.0, 0.3, p.color('ink.1'), place(-6.2, 0, 0));
    mb.box(0.3, 2.0, 0.3, p.color('ink.1'), place(6.2, 0, 0));
    mb.box(4.6, 0.3, 0.3, p.color('ink.1'), place(-4.0, 2.6, 0, 0, 1, 0, 0.5));
    mb.box(4.6, 0.3, 0.3, p.color('ink.1'), place(4.0, 2.6, 0, 0, 1, 0, -0.5));
    mb.box(4.2, 0.3, 0.3, p.color('enemyAccent.0'), place(0, 3.7, 0));
  });
  debris(mb, p, rng, 8, 6, halfD - 1);
  return mb;
};

const SPECIFIC: Partial<Record<PropKind, KitBuilder>> = {
  aircraft: aircraftWreck, samLauncher: samWreck, radar: radarWreck, pumpStation: pumpWreck, hangar: hangarWreck,
};

/**
 * The expansion's wrecks (10 §2; wrecksVehicles.ts, wrecksWorks.ts). A table of their own because they
 * answer to their own rules (tests/unit/kit3Wrecks.test.ts): a sunk barge, a mast fallen well outside its foot.
 */
const EXPANSION: Partial<Record<PropKind, KitBuilder>> = {
  locomotive: locomotiveWreck, fuelWagon: fuelWagonWreck, mobileLauncher: mobileLauncherWreck, staffCar: staffCarWreck,
  barge: bargeWreck, bridgePier: bridgePierWreck, casemate: casemateWreck, fieldGun: fieldGunWreck, gunPit: gunPitWreck,
  relayMast: relayMastWreck, generatorHut: generatorHutWreck, laboratory: laboratoryWreck, safe: safeWreck,
};

/**
 * The gun positions (ROUND4 §3, wrecksField.ts). Generic rubble over an emplacement's footprint read as
 * "quiet" rather than "finished" - worst of all for the MG nest, whose first collider box is a 3.8 x 0.7
 * strip, so its rubble covered only the front row of bags.
 */
const FIELD: Partial<Record<PropKind, KitBuilder>> = {
  bunker: bunkerWreck, turretBunker: turretBunkerWreck, mgNest: mgNestWreck,
};

/** True for the five original mission targets, whose wrecks are held to the old footprint (kitModels.test.ts). */
export function hasOwnWreck(kind: PropKind): boolean {
  return SPECIFIC[kind] !== undefined;
}

/** True for the expansion's kinds that have a wreck of their own. */
export function hasExpansionWreck(kind: PropKind): boolean {
  return EXPANSION[kind] !== undefined;
}

/** True for the emplacements, whose wreck has to differ from the same thing standing there empty. */
export function hasFieldWreck(kind: PropKind): boolean {
  return FIELD[kind] !== undefined;
}

/**
 * True when the wreck is modelled in the prop's frame - any wreck of its own - so it must take the prop's yaw.
 * That includes the wall's breach (ROUND7): a compound's east and west faces are pieces turned a quarter, and
 * generic rubble laid square to the world lay ACROSS the line of the wall there.
 */
export function wreckTurnsWithProp(kind: PropKind): boolean {
  return hasOwnWreck(kind) || hasExpansionWreck(kind) || hasFieldWreck(kind) || kind === 'wall';
}

/**
 * Wreckage for a destroyed prop: its own if it has one, else generic rubble over the footprint. A wall piece
 * is a breach the length of the piece (`halfX` is half its length: a run's collider is as long as the run).
 */
export function buildWreck(kind: PropKind, p: Palette, rng: Pcg32, halfX: number, halfZ: number): MeshBuilder {
  if (kind === 'wall') return wallBreach(p, rng, halfX * 2);
  const own = SPECIFIC[kind] ?? EXPANSION[kind] ?? FIELD[kind];
  return own ? own(p, rng) : wreck(p, rng, halfX, halfZ);
}
